import React, { useState, useEffect } from 'react';
import Breadcrumb from '../components/layout/Breadcrumb';
import Card from '../components/ui/Card';
import { Building2, Users, Recycle, TrendingUp, Package, DollarSign } from 'lucide-react';
import dashboardAPI from '../services/dashboard';

const Centers = () => {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);

  useEffect(() => {
    const fetchCentersData = async () => {
      try {
        setLoading(true);
        // Fetch detailed center data with relations
        const centerData = await dashboardAPI.getCentersData();
        setCenters(centerData);
      } catch (err) {
        console.error('Error fetching centers data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCentersData();
  }, []);

  // Calculate center statistics
  const calculateCenterStats = (center) => {
    const wasteCollected = center.newWasteCollecteds?.reduce((sum, waste) => sum + (waste.amount || 0), 0) || 0;
    const customers = center.newCustomerLists?.length || 0;
    const aggregatedWaste = center.aggregatedWastes?.reduce((sum, waste) => sum + (waste.totalAmount || 0), 0) || 0;
    const soldWaste = center.soldWastes?.reduce((sum, waste) => sum + (waste.amount || 0), 0) || 0;
    const revenue = center.soldWastes?.reduce((sum, waste) => sum + (waste.amount || 0) * (waste.pricePerKg || 50), 0) || 0;

    return {
      wasteCollected,
      customers,
      aggregatedWaste,
      soldWaste,
      revenue
    };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumb />
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading centers data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Breadcrumb />
        <div className="text-center py-12">
          <p className="text-red-600">Error loading centers data: {error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb />

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Centers Management</h1>
        <p className="mt-1 text-sm text-gray-600">
          View and manage all processing centers and their associated data
        </p>
      </div>

      {/* Center Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {centers.map((center) => {
          const stats = calculateCenterStats(center);
          
          return (
            <Card 
              key={center.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedCenter(selectedCenter?.id === center.id ? null : center)}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {center.centerName || `Center ${center.id}`}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {center.address || 'Address not available'}
                  </p>
                </div>
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <Recycle className="h-4 w-4 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{stats.wasteCollected.toFixed(4)} kg</p>
                    <p className="text-xs text-gray-600">Waste Collected</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Users className="h-4 w-4 text-purple-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{stats.customers}</p>
                    <p className="text-xs text-gray-600">Customers</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Package className="h-4 w-4 text-emerald-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{stats.aggregatedWaste} kg</p>
                    <p className="text-xs text-gray-600">Aggregated</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <DollarSign className="h-4 w-4 text-indigo-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">₹{stats.revenue.toLocaleString()}</p>
                    <p className="text-xs text-gray-600">Revenue</p>
                  </div>
                </div>
              </div>

              {selectedCenter?.id === center.id && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Detailed Information</h4>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Aggregated Waste:</span>
                      <span className="font-medium">{stats.aggregatedWaste} kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sold Waste:</span>
                      <span className="font-medium">{stats.soldWaste} kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Available Stock:</span>
                      <span className="font-medium">{stats.aggregatedWaste - stats.soldWaste} kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Revenue:</span>
                      <span className="font-medium">₹{stats.revenue.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Show waste types if available */}
                  {center.aggregatedWastes && center.aggregatedWastes.length > 0 && (
                    <div className="mt-3">
                      <h5 className="text-xs font-medium text-gray-700 mb-1">Waste Types:</h5>
                      <div className="flex flex-wrap gap-1">
                        {center.aggregatedWastes.map((waste, idx) => (
                          <span 
                            key={idx} 
                            className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded"
                          >
                            {waste.name}: {waste.totalAmount.toFixed(2)}kg - {new Date(waste.date).toLocaleDateString()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {centers.length === 0 && (
        <Card>
          <div className="text-center py-8">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No centers found</h3>
            <p className="text-gray-600">There are no processing centers in the system.</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Centers;